import Image from '@/components/Image';
import { baseURL } from '@/config/base';
import { useLocales } from '@/Locales';
import { apiPrefix } from '@/services';
import { getImageFileSize } from '@/utils';
import { LoadingOutlined, PlusOutlined } from '@ant-design/icons';
import { Upload, UploadProps, message } from '@sinohealth/butterfly-ui-components';
import { useState } from 'react';
import styles from './index.less';

// MIME 类型映射
const mimeMap: Record<string, string> = {
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.png': 'image/png',
  '.gif': 'image/gif',
  '.webp': 'image/webp',
  '.bmp': 'image/bmp',
  '.svg': 'image/svg+xml',
};

/**
 * 校验文件类型是否符合 accept 要求
 * @param file 文件对象
 * @param accept 接受的文件类型（支持 MIME 类型和扩展名）
 */
const validateFileType = (file: File, accept: string): boolean => {
  const acceptList = accept.split(',').map((item) => item.trim().toLowerCase());

  // 检查是否包含通配符（如 image/*）
  if (acceptList.some((item) => item.includes('*'))) {
    const baseType = acceptList.find((item) => item.includes('*'))?.split('/')[0];
    if (baseType && file.type.startsWith(`${baseType}/`)) {
      return true;
    }
  }

  // 检查 MIME 类型
  if (acceptList.some((item) => item.includes('/'))) {
    if (acceptList.includes(file.type.toLowerCase())) {
      return true;
    }
  }

  // 检查扩展名
  const fileExt = `.${file.name.split('.').pop()?.toLowerCase()}`;
  if (acceptList.some((item) => item.startsWith('.'))) {
    if (acceptList.includes(fileExt)) {
      return true;
    }
    // 扩展名转 MIME 类型检查
    const mime = mimeMap[fileExt];
    if (mime && acceptList.includes(mime)) {
      return true;
    }
  }

  return false;
};

interface CellUploadProps extends UploadProps {
  maxSize?: any;
  minDimension?: any; // 最小图片宽高
  maxDimension?: any; // 最大图片宽高
  onUploadSuccess?: any;
  value?: string;
  noStyle?: boolean;
  accept?: string;
}
const CellUpload = (props: CellUploadProps) => {
  const {
    onUploadSuccess,
    children,
    value,
    onChange,
    maxSize,
    maxDimension,
    minDimension,
    noStyle,
    accept,
    ...otherProps
  } = props;
  const [uploading, setUploading] = useState(false);
  const { t } = useLocales();
  const uploadParams: UploadProps = {
    name: 'file',
    showUploadList: false,
    listType: 'picture-card',
    action: `${baseURL + apiPrefix}/cos/upload`,
    accept: accept || 'image/*',
    disabled: uploading,
    beforeUpload: async (file: any) => {
      const currentAccept = accept || 'image/*';
      // 校验文件类型是否符合 accept 要求
      if (!validateFileType(file, currentAccept)) {
        const acceptTip = currentAccept.replace(/,/g, ' ').replace(/image\//g, ' ');
        message.error(`${t('只能上传')}${acceptTip}${t('格式的文件!')}`);
        return false;
      }
      if (file.type.indexOf('image') > -1 && (maxDimension || minDimension)) {
        const imgDimension: any = await getImageFileSize(file);
        if (imgDimension.width > maxDimension || imgDimension.height > maxDimension) {
          message.error(`${t('图片宽/高不能大于')}${maxDimension}!`);
          return false;
        }
        if (imgDimension.width < minDimension || imgDimension.height < minDimension) {
          message.error(`${t('图片宽/高不能小于')}${minDimension}!`);
          return false;
        }
      }
      if (maxSize) {
        const isLimit = file.size / 1024 / 1024 < maxSize;
        if (!isLimit) {
          message.error(`${t('上传文件不能大于')}${maxSize}MB!`);
          return false;
        }
      }
      setUploading(true);
      return true;
    },
    onChange(info: any) {
      const { status } = info.file;
      // 所有文件都上传完才修改loading状态
      setUploading(info.fileList.map((item: any) => item.status).includes('uploading'));
      if (status === 'done') {
        try {
          onUploadSuccess ? onUploadSuccess(info) : info.file?.response?.data;
          onChange && onChange(info.file?.response?.data);
        } catch (e) {
          console.error(e);
        }
      }
    },
    ...otherProps,
  };
  return (
    <Upload className={noStyle ? '' : styles.upload} {...uploadParams}>
      {children || (
        <div className={styles.content}>
          {
            // eslint-disable-next-line no-nested-ternary
            uploading ? (
              <>
                <LoadingOutlined style={{ fontSize: 18 }} />
                <div style={{ marginTop: 8 }}>{t('上传中...')}</div>
              </>
            ) : value ? (
              <Image key={value} src={value} className={styles.img} />
            ) : (
              <PlusOutlined />
            )
          }
        </div>
      )}
    </Upload>
  );
};

export default CellUpload;
